import {type EventData, type EventMap, type EventResponse, UnhandledEventError} from "./events.js";

/**
 * An event travelling along a listenable chain. Notification events ignore `resolve`;
 * request events created by `triggerOnce` settle the promise returned to the trigger site.
 */
export interface JuisEvent<TData, TResponse> {
    readonly type: string;
    readonly data: TData;
    readonly origin: EventNode;
    readonly propagationStopped: boolean;
    readonly settled: boolean;

    stopPropagation(): void;

    resolve(response: TResponse): void;

    reject(reason: unknown): void;
}

export interface Listener<TData, TResponse> {
    readonly destructed: boolean;
    readonly paused: boolean;

    /** Only invoke the handler when every registered predicate passes. */
    filter(predicate: (data: TData, event: JuisEvent<TData, TResponse>) => boolean): this;

    pause(): this;

    resume(): this;

    /** Skip exactly one upcoming invocation. */
    pauseOnce(): this;

    destruct(): void;
}

export interface ListenOptions {
    /** Destructs the listener when the signal aborts. */
    signal?: AbortSignal;
}

export interface TriggerOptions {
    /** Do not invoke listeners on the triggering node; start propagation at `nextListenable`. */
    skipOrigin?: boolean;
    /**
     * Remember the event on every node it visits, so listeners registered later
     * are invoked immediately with the most recent event of this type.
     */
    persistent?: boolean;
}

type RawHandler = (data: unknown, event: JuisEvent<unknown, unknown>) => void;
type RawPredicate = (data: unknown, event: JuisEvent<unknown, unknown>) => boolean;

class JuisEventImpl<TData, TResponse> implements JuisEvent<TData, TResponse> {
    propagationStopped = false;
    settled = false;
    readonly persistent: boolean;

    readonly #deferred: {
        resolve: (response: TResponse) => void;
        reject: (reason: unknown) => void;
    } | undefined;

    constructor(
        readonly type: string,
        readonly data: TData,
        readonly origin: EventNode,
        persistent: boolean,
        deferred?: { resolve: (response: TResponse) => void; reject: (reason: unknown) => void }
    ) {
        this.persistent = persistent;
        this.#deferred = deferred;
    }

    stopPropagation(): void {
        this.propagationStopped = true;
    }

    resolve(response: TResponse): void {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.#deferred?.resolve(response);
    }

    reject(reason: unknown): void {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.#deferred?.reject(reason);
    }
}

class ListenerImpl<TData, TResponse> implements Listener<TData, TResponse> {
    destructed = false;
    paused = false;
    #skipNext = false;
    readonly #predicates: RawPredicate[] = [];

    constructor(
        readonly type: string,
        readonly handler: RawHandler,
        readonly onDestruct: (listener: ListenerImpl<TData, TResponse>) => void
    ) {
    }

    filter(predicate: (data: TData, event: JuisEvent<TData, TResponse>) => boolean): this {
        this.#predicates.push(predicate as RawPredicate);
        return this;
    }

    pause(): this {
        this.paused = true;
        return this;
    }

    resume(): this {
        this.paused = false;
        return this;
    }

    pauseOnce(): this {
        this.#skipNext = true;
        return this;
    }

    destruct(): void {
        if (this.destructed) {
            return;
        }
        this.destructed = true;
        this.onDestruct(this);
    }

    accepts(event: JuisEvent<unknown, unknown>): boolean {
        if (this.destructed || this.paused) {
            return false;
        }
        if (this.#skipNext) {
            this.#skipNext = false;
            return false;
        }
        return this.#predicates.every(predicate => predicate(event.data, event));
    }
}

/**
 * Untyped event plumbing. Chain traversal lives here so that nodes with different
 * event maps can be linked without widening the typed API in {@link Listenable}.
 */
export class EventNode {
    nextListenable: EventNode | undefined;

    readonly #listeners = new Map<string, ListenerImpl<unknown, unknown>[]>();
    readonly #persistentEvents = new Map<string, JuisEventImpl<unknown, unknown>>();

    removeAllListeners(): void {
        for (const listeners of this.#listeners.values()) {
            for (const listener of [...listeners]) {
                listener.destruct();
            }
        }
        this.#listeners.clear();
    }

    protected registerListener(type: string, handler: RawHandler, options?: ListenOptions): ListenerImpl<unknown, unknown> {
        const listener = new ListenerImpl<unknown, unknown>(type, handler, removed => this.#removeListener(removed));
        const existing = this.#listeners.get(type);
        if (existing) {
            existing.push(listener);
        } else {
            this.#listeners.set(type, [listener]);
        }
        options?.signal?.addEventListener("abort", () => listener.destruct(), {once: true});
        const persistentEvent = this.#persistentEvents.get(type);
        if (persistentEvent && listener.accepts(persistentEvent)) {
            handler(persistentEvent.data, persistentEvent);
        }
        return listener;
    }

    /** Propagates the event along the chain and returns how many handlers were invoked. */
    protected dispatch(event: JuisEventImpl<unknown, unknown>, skipOrigin: boolean): number {
        let node: EventNode | undefined = skipOrigin ? this.nextListenable : this;
        let invocations = 0;
        while (node && !event.propagationStopped) {
            invocations += node.#invokeLocal(event);
            node = node.nextListenable;
        }
        return invocations;
    }

    #invokeLocal(event: JuisEventImpl<unknown, unknown>): number {
        if (event.persistent) {
            this.#persistentEvents.set(event.type, event);
        }
        const listeners = this.#listeners.get(event.type);
        if (!listeners) {
            return 0;
        }
        let invocations = 0;
        for (const listener of [...listeners]) {
            if (event.propagationStopped) {
                break;
            }
            if (!listener.accepts(event)) {
                continue;
            }
            invocations++;
            listener.handler(event.data, event);
        }
        return invocations;
    }

    #removeListener(listener: ListenerImpl<unknown, unknown>): void {
        const listeners = this.#listeners.get(listener.type);
        if (!listeners) {
            return;
        }
        const index = listeners.indexOf(listener);
        if (index >= 0) {
            listeners.splice(index, 1);
        }
        if (listeners.length === 0) {
            this.#listeners.delete(listener.type);
        }
    }
}

/**
 * Typed event emitter used by components, routers, models and services alike.
 * It is deliberately independent of the DOM; `CustomEvent` interop belongs in an adapter.
 */
export class Listenable<M extends EventMap> extends EventNode {
    on<K extends keyof M & string>(
        type: K,
        handler: (data: EventData<M, K>, event: JuisEvent<EventData<M, K>, EventResponse<M, K>>) => void,
        options?: ListenOptions
    ): Listener<EventData<M, K>, EventResponse<M, K>> {
        return this.registerListener(type, handler as RawHandler, options) as Listener<EventData<M, K>, EventResponse<M, K>>;
    }

    listenOnce<K extends keyof M & string>(
        type: K,
        handler: (data: EventData<M, K>, event: JuisEvent<EventData<M, K>, EventResponse<M, K>>) => void,
        options?: ListenOptions
    ): Listener<EventData<M, K>, EventResponse<M, K>> {
        const listener: Listener<EventData<M, K>, EventResponse<M, K>> = this.on(type, (data, event) => {
            listener.destruct();
            handler(data, event);
        }, options);
        return listener;
    }

    when<K extends keyof M & string>(type: K, options?: ListenOptions): Promise<EventData<M, K>> {
        return new Promise(resolve => {
            this.listenOnce(type, data => resolve(data), options);
        });
    }

    trigger<K extends keyof M & string>(type: K, data: EventData<M, K>, options?: TriggerOptions): void {
        const event = new JuisEventImpl<unknown, unknown>(type, data, this, options?.persistent ?? false);
        this.dispatch(event, options?.skipOrigin ?? false);
    }

    /**
     * Triggers a request event. The returned promise settles when a listener calls
     * `event.resolve(...)`, and rejects immediately if no listener handled the event at all.
     */
    triggerOnce<K extends keyof M & string>(
        type: K,
        data: EventData<M, K>,
        options?: TriggerOptions
    ): Promise<EventResponse<M, K>> {
        let resolveFn!: (response: unknown) => void;
        let rejectFn!: (reason: unknown) => void;
        const promise = new Promise<unknown>((resolve, reject) => {
            resolveFn = resolve;
            rejectFn = reject;
        });
        const event = new JuisEventImpl<unknown, unknown>(type, data, this, options?.persistent ?? false, {
            resolve: resolveFn,
            reject: rejectFn
        });
        const invocations = this.dispatch(event, options?.skipOrigin ?? false);
        if (invocations === 0) {
            event.reject(new UnhandledEventError(type));
        }
        return promise as Promise<EventResponse<M, K>>;
    }

    /** Re-triggers every event of this type on another listenable, forwarding its resolution. */
    redirect<K extends keyof M & string, M2 extends EventMap & { [P in K]: M[K] }>(
        type: K,
        target: Listenable<M2>
    ): Listener<EventData<M, K>, EventResponse<M, K>> {
        return this.on(type, (data, event) => {
            event.stopPropagation();
            target.triggerOnce(type, data as EventData<M2, K>).then(
                response => event.resolve(response as EventResponse<M, K>),
                reason => event.reject(reason)
            );
        });
    }
}
